'use client';

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/utils';
import { initializeCollectJS, configureCollectJS, PaymentError } from '@/lib/nmi';
import { StripeCheckoutInline } from '@/components/payments/StripeCheckoutInline';
import { PayArcTokenization } from '@/components/payments/PayArcTokenization';
import { getCompanySettings } from '@/lib/api';
import { CreditCard, Loader2, Lock, Shield } from 'lucide-react';
import { showNotification } from '@/lib/generalNotifications';
import { PaymentErrorDialog } from '@/components/PaymentErrorDialog';
import { showTransactionSuccess, showTransactionError } from '@/lib/notifications';
import type { Invoice } from '@/types/firebase';
import { useQueryClient } from '@tanstack/react-query';

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Partial<Invoice> & { 
    id: string;
    // Add individual address fields that might be available
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    // Add donation-specific fields
    country?: string;
    dynamicFields?: Record<string, string>;
  };
  invoiceAmount: number;
  platformFee: number;
  enablePlatformFee?: boolean;
  onPaymentSuccess?: (transactionId: string, cardLast4: string, paymentMethod: string) => void;
  salesTax?: number;
  enableSalesTax?: boolean;
  // Donation context (used by Stripe flow to post to donation API)
  donationBaseAmount?: number;
  donationDynamicFields?: Record<string, string>;
}

export function PaymentModal({
  open,
  onOpenChange,
  invoice,
  invoiceAmount,
  platformFee,
  enablePlatformFee = false,
  onPaymentSuccess,
  salesTax = 0,
  enableSalesTax = false,
  donationBaseAmount,
  donationDynamicFields
}: PaymentModalProps) {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCollectJSInitialized, setIsCollectJSInitialized] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  
  // Calculate platform fee amount and total with tax
  const taxAmount = enableSalesTax ? salesTax : 0;
  const subtotalWithTax = invoiceAmount + taxAmount;
  const platformFeeAmount = enablePlatformFee && platformFee > 0 ? (subtotalWithTax * platformFee) / 100 : 0;
  const totalWithFee = subtotalWithTax + platformFeeAmount;
  const invoiceId = typeof invoice?.id === 'string' ? invoice.id : '';
  const hasInvoiceId = invoiceId.length > 0;
  const isVirtualTerminalInvoice = invoiceId.startsWith('virtual-terminal-');
  const virtualTerminalDescription =
    (typeof (invoice as any).paymentDescription === 'string'
      ? (invoice as any).paymentDescription.trim()
      : '') || 'Virtual Terminal Payment';
  const donationDescription =
    invoiceId.startsWith('donation-') && typeof (invoice as any).paymentDescription === 'string'
      ? (invoice as any).paymentDescription.trim()
      : '';
  
  // Keep track of the modal's open state
  const isOpenRef = useRef(open);
  
  // Check if the invoice is a recurring or installment type
  const isRecurringOrInstallment = invoice.invoiceType === 'recurring' || invoice.invoiceType === 'installment';
  const [defaultCardProvider, setDefaultCardProvider] = useState<'nmi'|'stripe'|'payarc'|null>(null);


  useEffect(() => {
    (async () => {
      try {
        if (!invoice.companyId) return;
        const settings = await getCompanySettings(invoice.companyId);
        const card = settings.paymentGateways?.defaults?.card as any;
        const stripeEnabled = settings.paymentGateways?.providers?.stripe?.enabled !== false;
        if (card === 'stripe' && stripeEnabled) {
          setDefaultCardProvider('stripe');
        } else if (card === 'nmi') {
          setDefaultCardProvider('nmi');
        } else if (card === 'payarc') {
          setDefaultCardProvider('payarc');
        } else {
          setDefaultCardProvider('nmi');
        }
      } catch {
        // On error, default to NMI to avoid unintended provider switches
        setDefaultCardProvider('nmi');
      }
    })();
  }, [invoice.companyId]);
  
  useEffect(() => {
    isOpenRef.current = open;
  }, [open]);

  // Initialize CollectJS when the modal opens
  useEffect(() => {
    // Skip initialization if the modal is not open
    if (!open) return;
    // Only initialize CollectJS when explicitly using NMI
    if (defaultCardProvider !== 'nmi') return;
    
    // Skip initialization if already initialized
    if (isCollectJSInitialized) {
      return;
    }
    
    let initializationTimeout: NodeJS.Timeout;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const handleTokenError = () => {
      // Token generation error
      setErrorCode('TOKEN_ERROR');
      setIsErrorDialogOpen(true);
      setIsProcessing(false);
    };

    const setupCollectJS = async () => {
      try {
        const cleanup = () => {
          // Remove any existing CollectJS scripts
          const existingScript = document.querySelector('script[src*="Collect.js"]');
          if (existingScript) existingScript.remove();
          
          // Remove any existing Apple Pay scripts
          const applePayScript = document.querySelector('script[src*="apple-pay-sdk"]');
          if (applePayScript) applePayScript.remove();
          
          // Remove bootstrap autofill overlays that might cause issues
          try {
            const autofillOverlays = document.querySelectorAll('[class*="bootstrap-autofill-overlay"]');
            autofillOverlays.forEach(overlay => overlay.remove());
          } catch {
            // Ignore errors
          }
          
          // Remove apple-spinner elements if they exist
          try {
            // Check if the element is already defined in the registry
            const existingSpinner = document.querySelector('apple-spinner');
            if (existingSpinner) {
              existingSpinner.remove();
            }
            
            // Try to undefine the custom element if possible
            if (window.customElements && 
                window.customElements.get && 
                window.customElements.get('apple-spinner')) {
              // We can't truly undefine it, but we can remove instances
            }
          } catch {
            // We can't truly undefine it, but we can remove instances
          }
        };

        cleanup();
        setIsCollectJSInitialized(false);

        // Wait longer to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 1500));

        if (!open) return;

        const waitForPaymentFields = new Promise<void>((resolve, reject) => {
          const checkFields = setInterval(() => {
            const ccnumber = document.getElementById('ccnumber');
            const ccexp = document.getElementById('ccexp');
            const cvv = document.getElementById('cvv');

            if (ccnumber && ccexp && cvv) {
              clearInterval(checkFields);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkFields);
            reject(new Error('Payment fields not found or not ready'));
          }, 5000);
        });

        await waitForPaymentFields;

        if (!open) return;

        await initializeCollectJS(invoice.companyId);
        setIsCollectJSInitialized(true);

        if (!open) return;

        // Initializing CollectJS
        configureCollectJS({
          amount: totalWithFee,
          orderId: `INV-${invoice.invoiceNumber}-${Date.now()}`,
          orderDescription: isVirtualTerminalInvoice
            ? virtualTerminalDescription
            : `Payment for Invoice #${invoice.invoiceNumber}`,
          ...(hasInvoiceId ? { invoiceId } : {}),
          companyId: invoice.companyId,
          firstName: invoice.customerName?.split(' ')[0] || '',
          lastName: invoice.customerName?.split(' ')[1] || '',
          email: invoice.customerEmail || '',
          address: invoice.street || (invoice.customerAddress ? invoice.customerAddress.split(',')[0] : ''),
          city: invoice.city || '',
          state: invoice.state || '',
          zip: invoice.zip || '',
        }, handlePaymentToken, handleTokenError);

      } catch {
        if (!open) return;
        
        // Failed to initialize payment form
        setIsCollectJSInitialized(false);
        
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          // Retrying initialization
          
          initializationTimeout = setTimeout(() => {
            if (open) {
              setupCollectJS();
            }
          }, 2000);
        } else {
          showNotification({
            title: "Error",
            message: "Failed to initialize payment form after multiple attempts. Please refresh the page.",
            type: "error"
          });
        }
      }
    };

    if (defaultCardProvider === 'nmi') {
      setupCollectJS();
    }

    return () => {
      setIsCollectJSInitialized(false);
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
      }
      const cleanup = () => {
        const script = document.querySelector('script[src*="Collect.js"]');
        if (script) script.remove();
        const applePayScript = document.querySelector('script[src*="apple-pay-sdk"]');
        if (applePayScript) applePayScript.remove();
        const existingSpinner = document.querySelector('apple-spinner');
        if (existingSpinner) {
          existingSpinner.remove();
        }
        // Clean up autofill overlays
        try {
          const autofillOverlays = document.querySelectorAll('[class*="bootstrap-autofill-overlay"]');
          autofillOverlays.forEach(overlay => overlay.remove());
        } catch {
          // Ignore errors
        }
      };
      cleanup();
    };
  }, [open, invoice.companyId, invoice.invoiceNumber, defaultCardProvider]);

  // Helper function to save payment method to Firebase
  const savePaymentMethodToFirebase = async (customerId: string, response: any) => {
    const { db } = await import('@/lib/firebase');
    const { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } = await import('firebase/firestore');
    const { auth } = await import('@/lib/firebase');
    
    // Only log non-sensitive information
    
    try {
      // Get company ID from current user or invoice
      let companyId = invoice.companyId;

      if (!companyId) {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated and no company ID provided');
        }
        
        companyId = user.uid.startsWith('company_') 
          ? user.uid 
          : `company_${user.uid}`;
      }
      
      // Check if the customer already has saved payment methods
      const paymentMethodsRef = collection(db, 'paymentMethods');

      const cardData = (window as any).__nmiCardData || {};

      const provider = (response.provider
        || (response.customer_vault_id ? 'nmi'
          : (response.paymentMethodId || response.stripePaymentMethodId ? 'stripe'
            : ((response.providerName || cardData.provider || 'nmi') as string))))
        .toLowerCase();

      const normalizedProvider = (provider === 'stripe' || provider === 'payarc') ? provider : 'nmi';

      const paymentMethodsQuery = query(
        paymentMethodsRef,
        where('customerId', '==', customerId),
        where('companyId', '==', companyId),
        where('provider', '==', normalizedProvider)
      );
      
      let existingPaymentMethods: any[] = [];
      try {
        const paymentMethodsSnapshot = await getDocs(paymentMethodsQuery);
        existingPaymentMethods = paymentMethodsSnapshot.empty ? [] : paymentMethodsSnapshot.docs;
      } catch {
        // Error querying existing payment methods
        // Continue with empty array if query fails
      }
      
      // Current timestamp for dates
      const now = serverTimestamp();

      if (normalizedProvider === 'nmi') {
        const cardLast4 = cardData.last4 || response.last_four || response.cardLast4 || response.last4 || 
                         (response.cc_number ? response.cc_number.slice(-4) : '') || 
                         (response.account_number ? response.account_number.slice(-4) : '') || '';
        const cardType = cardData.cardType || response.card_type || response.cardType || response.cc_type || '';
        const expiryMonth = cardData.expiryMonth || response.expiryMonth || '';
        const expiryYear = cardData.expiryYear || response.expiryYear || '';
        const nmiCustomerId = response.customer_vault_id || response.customerVaultId || response.vault_id || '';

        const formattedCardType = cardType ? cardType.charAt(0).toUpperCase() + cardType.slice(1) : 'Card';
        const nickName = `${formattedCardType} ending in ${cardLast4}`;

        if (nmiCustomerId) {
          try {
            const paymentMethodData = {
              customerId: customerId,
              companyId: companyId,
              type: 'credit_card' as const,
              provider: 'nmi' as const,
              last4: cardLast4,
              cardType: cardType || 'Unknown',
              expiryMonth: expiryMonth,
              expiryYear: expiryYear,
              isDefault: true,
              nmiCustomerId: nmiCustomerId,
              nmiToken: cardData.token || response.token || '',
              nickName: nickName,
              createdAt: now,
              updatedAt: now,
              lastUsedAt: now,
            };

            await addDoc(collection(db, 'paymentMethods'), paymentMethodData);

            if (existingPaymentMethods.length > 0) {
              await Promise.all(existingPaymentMethods.map(oldMethod =>
                deleteDoc(doc(db, 'paymentMethods', oldMethod.id))
              ));
            }
          } catch {
            if (existingPaymentMethods.length > 0) {
              try {
                await updateDoc(doc(db, 'paymentMethods', existingPaymentMethods[0].id), {
                  last4: cardLast4,
                  cardType: cardType || 'Unknown',
                  expiryMonth: expiryMonth,
                  expiryYear: expiryYear,
                  isDefault: true,
                  nickName: nickName,
                  updatedAt: now,
                  lastUsedAt: now,
                });
              } catch {}
            }
          }
        } else {
          console.warn('Skipping saved payment persistence: NMI did not return a customer_vault_id');
        }
      } else if (normalizedProvider === 'stripe') {
        const stripePaymentMethodId = response.paymentMethodId || response.stripePaymentMethodId;
        const stripeCustomerId = response.customerRef || response.stripeCustomerId;

        if (!stripePaymentMethodId || !stripeCustomerId) {
          console.warn('Skipping Stripe saved payment persistence: missing payment method or customer reference');
        } else {
          const cardLast4 = response.last4 || '';
          const cardType = response.brand || 'Unknown';
          const expiryMonth = response.expMonth || '';
          const expiryYear = response.expYear || '';
          const nickName = `${cardType ? cardType.toUpperCase() : 'CARD'} ending in ${cardLast4 || 'XXXX'}`;

          try {
            const paymentMethodData = {
              customerId: customerId,
              companyId: companyId,
              type: 'credit_card' as const,
              provider: 'stripe' as const,
              last4: cardLast4,
              cardType: cardType || 'Unknown',
              expiryMonth: expiryMonth,
              expiryYear: expiryYear,
              isDefault: true,
              stripeCustomerId: stripeCustomerId,
              stripePaymentMethodId: stripePaymentMethodId,
              nickName,
              createdAt: now,
              updatedAt: now,
              lastUsedAt: now,
            };

            await addDoc(collection(db, 'paymentMethods'), paymentMethodData);

            if (existingPaymentMethods.length > 0) {
              await Promise.all(existingPaymentMethods.map(oldMethod =>
                deleteDoc(doc(db, 'paymentMethods', oldMethod.id))
              ));
            }
          } catch {
            if (existingPaymentMethods.length > 0) {
              try {
                await updateDoc(doc(db, 'paymentMethods', existingPaymentMethods[0].id), {
                  last4: cardLast4,
                  cardType: cardType || 'Unknown',
                  expiryMonth: expiryMonth,
                  expiryYear: expiryYear,
                  isDefault: true,
                  nickName,
                  updatedAt: now,
                  lastUsedAt: now,
                  stripeCustomerId: stripeCustomerId,
                  stripePaymentMethodId: stripePaymentMethodId,
                });
              } catch {}
            }
          }
        }
      } else if (normalizedProvider === 'payarc') {
        const cardLast4 = response.last4 || response.last_four || '';
        const cardType = response.cardType || response.brand || 'Unknown';
        const expiryMonth = response.expMonth ? String(response.expMonth).padStart(2, '0') : (response.expiryMonth || '');
        const expiryYear = response.expYear ? String(response.expYear) : (response.expiryYear || '');
        const rawPayarcPaymentToken = response.payarcPaymentToken || response.payarcTokenId || response.payarcChargeId || null;
        const rawPayarcCustomerRef = response.payarcCustomerRef || response.payarcCustomerId || null;
        const payarcPaymentToken = rawPayarcPaymentToken ? String(rawPayarcPaymentToken) : null;
        const payarcCustomerRef = rawPayarcCustomerRef ? String(rawPayarcCustomerRef) : null;
        const nickName = `${cardType ? cardType : 'Card'} ending in ${cardLast4 || 'XXXX'}`;

        if (!payarcPaymentToken && !payarcCustomerRef) {
          console.warn('Skipping PayArc saved payment persistence: missing token or customer reference');
        } else {
          try {
            const paymentMethodData = {
              customerId: customerId,
              companyId: companyId,
              type: 'credit_card' as const,
              provider: 'payarc' as const,
              last4: cardLast4,
              cardType: cardType || 'Unknown',
              expiryMonth: expiryMonth,
              expiryYear: expiryYear,
              isDefault: true,
              payarcPaymentToken: payarcPaymentToken || undefined,
              payarcCustomerRef: payarcCustomerRef || undefined,
              nickName,
              createdAt: now,
              updatedAt: now,
              lastUsedAt: now,
            };

            await addDoc(collection(db, 'paymentMethods'), paymentMethodData);

            if (existingPaymentMethods.length > 0) {
              await Promise.all(existingPaymentMethods.map(oldMethod =>
                deleteDoc(doc(db, 'paymentMethods', oldMethod.id))
              ));
            }
          } catch {
            if (existingPaymentMethods.length > 0) {
              try {
                await updateDoc(doc(db, 'paymentMethods', existingPaymentMethods[0].id), {
                  last4: cardLast4,
                  cardType: cardType || 'Unknown',
                  expiryMonth: expiryMonth,
                  expiryYear: expiryYear,
                  isDefault: true,
                  nickName,
                  updatedAt: now,
                  lastUsedAt: now,
                  payarcPaymentToken: payarcPaymentToken || undefined,
                  payarcCustomerRef: payarcCustomerRef || undefined,
                });
              } catch {}
            }
          }
        }
      } else {
        console.warn(`Skipping saved payment persistence: unsupported provider ${normalizedProvider}`);
      }

      // Clear the card data from window for security
      if ((window as any).__nmiCardData) {
        (window as any).__nmiCardData = undefined;
      }
    } catch (error) {
      // Error saving payment method to Firebase
      // Don't throw the error to prevent notification
      console.error('Failed to persist payment method to Firebase:', error);
    }
  };

  const handlePaymentToken = async (token: string) => {
    // Don't log the actual token
    
    try {
      // Use the environment utility to get the correct API URL
      // Check if this is a donation payment
      const isDonation = invoiceId.startsWith('donation-');
      
      // Use unified donation charge for donations; unified charge for invoices
      const endpoint = isDonation ? '/api/donations/process' : '/api/payments/charge';
      const donationPayload = {
        companyId: invoice.companyId,
        amount: totalWithFee,
        currency: 'usd',
        tokenId: token,
        donationData: {
          companyId: invoice.companyId,
          amount: invoiceAmount,
          firstName: invoice.customerName?.split(' ')[0] || '',
          lastName: invoice.customerName?.split(' ')[1] || '',
          email: invoice.customerEmail || '',
          address: invoice.street || '',
          city: invoice.city || '',
          state: invoice.state || '',
          zip: invoice.zip || '',
          country: invoice.country || '',
          platformFeeEnabled: enablePlatformFee,
          platformFeeAmount: platformFeeAmount,
          platformFeePercentage: platformFee,
          paymentMethod: 'credit_card',
          dynamicFields: invoice.dynamicFields || {},
          slug: (invoice as any).slug || (invoice.dynamicFields?.slug) || '',
          description: donationDescription,
          metadata: {
            source: 'payment_modal',
            ...(hasInvoiceId ? { invoiceId } : {}),
          },
        },
      };

      const standardPayload = {
        companyId: invoice.companyId,
        amount: totalWithFee,
        currency: 'usd',
        source: 'new',
        tokenId: token,
        description: isVirtualTerminalInvoice
          ? virtualTerminalDescription
          : 'Payment for Invoice #' + (invoice.invoiceNumber || ''),
        orderId: `INV-${invoice.invoiceNumber}-${Date.now()}`,
        ...(hasInvoiceId ? { invoiceId } : {}),
        customerId: invoice.customerId,
        customerName: invoice.customerName,
        customerEmail: invoice.customerEmail,
        savePaymentMethod: savePaymentMethod,
        platformFeeEnabled: enablePlatformFee,
        platformFeePercentage: platformFee,
        platformFeeAmount: platformFeeAmount,
        salesTax: taxAmount,
        kind: 'card',
        returnUrl: window.location.href,
      };

      const payload = isDonation ? donationPayload : standardPayload;

      const apiResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      // Parse the response
      const response = await apiResponse.json();
      // Extract the transaction ID, checking all common field names
      const responseTransactionId = response.transactionid || response.transaction_id || 
                                   response.transactionId || response.id || '';
      
      if (!responseTransactionId && response.success) {
        // Warning: Successful payment without transaction ID returned from API
      }
      
      if (response.success) {
        // Save payment method if requested
        if (savePaymentMethod) {
          try {
            await savePaymentMethodToFirebase(invoice.customerId || '', response);
          } catch (error) {
            // Error saving payment method
            // Don't show notification for vault ID errors
            console.error('Failed to vault payment method after successful charge:', error);
          }
        }
        
        // Get card details for the notification
        const cardLast4 = response.last4 || response.cc_number?.slice(-4) || '';
        
        // Store the transaction success details
        const transactionId = responseTransactionId;
        
        // Unified charge API already records a standardized transaction server-side
        
        // Then update invoice status to paid with platform fee details
        try {
          // Skip invoice update for donations and virtual terminal as they don't have real invoices in the database
          if (hasInvoiceId && !invoiceId.startsWith('donation-') && !invoiceId.startsWith('virtual-terminal-')) {
            const { updateInvoice } = await import('@/lib/api');
            await updateInvoice(invoiceId, {
              status: 'paid',
              paymentTransactionId: transactionId,
              platformFee: enablePlatformFee ? platformFeeAmount : 0,  // Explicitly set platform fee
              activities: [{
                id: `payment_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                type: 'paid',
                timestamp: new Date().toISOString(),
                description: `Invoice paid - Transaction ID: ${transactionId}` + 
                  (enablePlatformFee ? ` (Platform fee: ${formatCurrency(platformFeeAmount)})` : ''),
                performedBy: 'customer',
                metadata: {
                  transactionId: transactionId,
                  platformFee: platformFeeAmount,
                  platformFeePercentage: platformFee
                }
              }]
            });
            
            // Invoice marked as paid with platform fee
          } else {
            // For donations, just log success - email is handled server-side in donation-process API
            // Donation payment processed successfully
          }
        } catch (error) {
          // Error updating invoice status with platform fee
          console.error('Failed to update invoice status after payment:', error);
        }
        
        const handleSuccessClose = async () => {
          // Reset processing state immediately
          setIsProcessing(false);
          
          // Invalidate React Query caches to ensure fresh data
          await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
          await queryClient.invalidateQueries({ queryKey: ['transactions'], exact: false });
          if (hasInvoiceId) {
            await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
          }
          
          // Close the payment modal
          onOpenChange(false);
          
          // Call the payment success callback AFTER all processing is complete
          if (onPaymentSuccess) {
            onPaymentSuccess(transactionId, cardLast4, 'credit_card');
          }
          
          // For regular invoices, redirect to the invoice page based on the invoice type
          if (invoiceId.startsWith('virtual-terminal-')) {
            // For virtual terminal, just close modal (parent component will handle data refresh)
            // No reload needed for SPA behavior
          } else if (hasInvoiceId && !invoiceId.startsWith('donation-')) {
            // For regular invoices, redirect to the invoice page
            window.location.href = `/pay/${invoiceId}`;
          }
        };
        
        // Show transaction success notification with callback to handle refresh
        showTransactionSuccess({
          transactionid: transactionId,
          amount: totalWithFee,
          cardLast4: cardLast4,
          additionalMessage: '',
          onClose: handleSuccessClose
        });
      } else {
        // Payment failed - determine the most specific error code
        let errorCode = response.response_code || 'PROCESSING_ERROR';
        
        // Check for CVV-specific errors (CVV mismatch is a common failure reason)
        if (response.cvvresponse && response.cvvresponse !== 'M') {
          errorCode = `CVV_${response.cvvresponse}`;
        }
        // Check for AVS-specific errors (address mismatch)
        else if (response.avsresponse && ['N', 'A', 'Z', 'W'].includes(response.avsresponse)) {
          errorCode = `AVS_${response.avsresponse}`;
        }
        
        // Log the error details for debugging
        // Payment failed - Full Response and Error Details
        
        // Don't pass the NMI message - let our error display system use the proper user-friendly message
        throw new PaymentError('', errorCode);
      }
    } catch (error) {
      // Payment error
      
      let errorCode = 'UNKNOWN_ERROR';
      let errorMessage = 'An unexpected error occurred while processing your payment.';
      
      if (error instanceof PaymentError) {
        errorCode = error.code;
        errorMessage = error.message;
        
        // Create a failed transaction record for tracking purposes
        try {
          const { createFailedTransaction } = await import('@/lib/transactions');
          
          await createFailedTransaction({
            amount: totalWithFee,
            customerId: invoice.customerId || '',
            companyId: invoice.companyId || '',
            transactionId: '', // No transaction ID for failed payments
            description: invoiceId.startsWith('virtual-terminal-') 
              ? 'Failed Virtual Terminal Payment' 
              : `Failed Payment for Invoice #${invoice.invoiceNumber}`,
            customerName: invoice.customerName || '',
            date: new Date().toISOString().split('T')[0],
            platformFeeEnabled: enablePlatformFee,
            platformFeePercentage: platformFee,
            platformFeeAmount: 0, // No fee charged for failed payments
            failureReason: errorMessage || 'Unknown error',
            paymentMethod: 'credit_card', // Required field
            // Add invoice details if not from virtual terminal
            ...(invoiceId.startsWith('virtual-terminal-') || !hasInvoiceId ? {} : { 
              invoiceId,
              invoiceNumber: invoice.invoiceNumber
            }),
            // Add error details to metadata
            additionalMetadata: {
              errorCode: errorCode || 'UNKNOWN_ERROR',
              errorMessage: errorMessage || 'Unknown error'
            }
          });
        } catch (transactionError) {
          // Error creating failed transaction record
          console.error('Failed to record failed transaction analytics:', transactionError);
        }
        
        // Send email notification if customer has an email
        if (invoice.customerEmail) {
          try {
            const { sendCardOnFileTransactionEmail } = await import('@/lib/emailNotifications');
            
            await sendCardOnFileTransactionEmail({
              transactionId: `failed_${Date.now()}`,
              amount: totalWithFee,
              status: 'declined',
              responseText: errorMessage,
              customerEmail: invoice.customerEmail,
              customerName: invoice.customerName || '',
              companyId: invoice.companyId || '',
              invoiceNumber: invoice.invoiceNumber,
              ...(hasInvoiceId ? { invoiceId } : {})
            });
          } catch (emailError) {
            // Error sending failed payment email notification
            // Continue even if email fails
            console.error('Failed to send failed payment notification email:', emailError);
          }
        }
        
        // Add a failed payment activity to the invoice if it's not a virtual terminal payment
        if (hasInvoiceId && !invoiceId.startsWith('virtual-terminal-') && !invoiceId.startsWith('donation-')) {
          try {
            const { updateInvoice } = await import('@/lib/api');
            
            await updateInvoice(invoiceId, {
              activities: [{
                id: `payment_failed_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                type: 'payment_failed',
                timestamp: new Date().toISOString(),
                description: `Payment failed - ${errorMessage}`,
                performedBy: 'customer',
                metadata: {
                  errorCode: errorCode,
                  errorDetails: errorMessage
                }
              }]
            });
          } catch (activityError) {
            // Error adding failed payment activity
            console.error('Failed to append failed payment activity:', activityError);
          }
        }
      }
      
      // Show only ONE error notification - use showTransactionError which has better formatting
      if (!errorMessage.toLowerCase().includes('vault') && !errorCode.toLowerCase().includes('vault')) {
        showTransactionError(error);
      }
      
      setIsProcessing(false);
    }
  };

  const handleCheckout = async () => {
    setIsProcessing(true);

    try {
      if (!isCollectJSInitialized || !(window as any).CollectJS) {
        throw new Error("Payment system not initialized. Please refresh the page.");
      }
      
      (window as any).CollectJS.startPaymentRequest();
    } catch (error) {
      // Checkout error
      setIsProcessing(false);
      
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "An unexpected error occurred",
        type: "error"
      });
    }
  };
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="sm:max-w-md p-0 overflow-hidden rounded-xl border-0 shadow-2xl"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <DialogHeader className="mb-2">
              <DialogTitle className="text-2xl font-bold text-white">Pay Invoice #{invoice.invoiceNumber}</DialogTitle>
            </DialogHeader>
          </div>
          
          <div className="px-6 py-5 bg-white">
            <div className="flex justify-between mb-6 pb-4 border-b border-gray-100">
              <div>
                <Label className="text-sm text-gray-500 font-medium">Invoice Amount</Label>
                <div className="text-xl font-semibold">{formatCurrency(invoiceAmount)}</div>
              </div>
              
              {salesTax > 0 && (
                <div>
                  <Label className="text-sm text-gray-500 font-medium">Sales Tax</Label>
                  <div className="text-xl font-semibold">{formatCurrency(salesTax)}</div>
                </div>
              )}
              
              {enablePlatformFee && platformFeeAmount > 0 && (
                <div>
                  <Label className="text-sm text-gray-500 font-medium">Processing Fee</Label>
                  <div className="text-xl font-semibold">{formatCurrency(platformFeeAmount)}</div>
                </div>
              )}
            </div>
            
            <div className="mb-6 pb-4 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold text-gray-700">Total</Label>
                <div className="text-2xl font-bold text-blue-700">{formatCurrency(totalWithFee)}</div>
              </div>
            </div>
            
            {defaultCardProvider === null ? (
              <div className="space-y-4 mb-6 text-sm text-gray-600">Loading payment form…</div>
            ) : defaultCardProvider === 'stripe' ? (
              <div className="space-y-4 mb-6">
            <StripeCheckoutInline
            companyId={invoice.companyId || ''}
            amount={totalWithFee}
            currency={'usd'}
            description={invoiceId.startsWith('donation-')
              ? donationDescription || `Donation payment - ${invoice.invoiceNumber}`
              : isVirtualTerminalInvoice
              ? virtualTerminalDescription
              : `Payment for Invoice #${invoice.invoiceNumber}`}
            orderId={invoiceId.startsWith('virtual-terminal-') ? `VT-${Date.now()}` : `INV-${invoice.invoiceNumber}`}
            invoiceId={hasInvoiceId ? invoiceId : undefined}
            customerId={invoice.customerId}
            customerEmail={invoice.customerEmail || ''}
            customerName={invoice.customerName || ''}
            // Pass address details to Stripe for billing/shipping
            address={invoice.street || ''}
            city={invoice.city || ''}
            state={invoice.state || ''}
            zip={invoice.zip || ''}
            country={invoice.country || 'US'}
            platformFeeEnabled={enablePlatformFee}
            platformFeePercentage={platformFee}
            platformFeeAmount={platformFeeAmount}
            salesTax={taxAmount}
            savePaymentMethod={savePaymentMethod}
            // Donation context when invoice id indicates donation
            donationBaseAmount={donationBaseAmount}
            donationDynamicFields={donationDynamicFields}
            publishableKey={(import.meta as any)?.env?.VITE_STRIPE_PUBLISHABLE_KEY
              || (import.meta as any)?.env?.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
              || (typeof process !== 'undefined' && (process as any)?.env?.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)
              || (window as any)?.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY}
              onSuccess={async (txId, last4) => {
                // Show success notification and wait for user acknowledgment before refreshing
                const handleSuccessClose = async () => {
                  // Reset processing state immediately
                  setIsProcessing(false);
                  onOpenChange(false);
                  if (onPaymentSuccess) onPaymentSuccess(txId, last4 || '', 'credit_card');
                  if (invoiceId.startsWith('virtual-terminal-')) {
                    // For virtual terminal, just close modal (parent component will handle data refresh)
                    // No reload needed for SPA behavior
                  } else if (hasInvoiceId && !invoiceId.startsWith('donation-')) {
                    window.location.href = `/pay/${invoiceId}`;
                  }
                };

                showTransactionSuccess({
                  transactionid: txId,
                  amount: totalWithFee,
                  cardLast4: last4 || '',
                  additionalMessage: '',
                  onClose: handleSuccessClose,
                });

                // Fire-and-forget: only for Virtual Terminal. For invoices/donations, the server sends the proper template.
                try {
                  const isVirtualTerminal = invoiceId.startsWith('virtual-terminal-');
                  if (isVirtualTerminal && invoice.customerEmail) {
                    const { sendCardOnFileTransactionEmail } = await import('@/lib/emailNotifications');
                    await sendCardOnFileTransactionEmail({
                      transactionId: txId,
                      amount: totalWithFee,
                      status: 'approved',
                      authCode: '',
                      cardLast4: last4 || '',
                      responseText: 'succeeded',
                      customerEmail: invoice.customerEmail,
                      customerName: invoice.customerName || '',
                      companyId: invoice.companyId || '',
                      invoiceNumber: String(invoice.invoiceNumber || ''),
                      invoiceId: hasInvoiceId ? invoiceId : undefined,
                    });
                  }
                } catch {}
              }}
              onError={() => {
                setErrorCode('PROCESSING_ERROR');
                setIsErrorDialogOpen(true);
              }}
            />
              </div>
            ) : defaultCardProvider === 'payarc' ? (
              <div className="space-y-4 mb-6">
                <PayArcTokenization
                  companyId={invoice.companyId || ''}
                  amount={totalWithFee}
                  savePaymentMethod={savePaymentMethod}
                  initialCardholderName={invoice.customerName || ''}
                  customerEmail={invoice.customerEmail || ''}
                  onTokenCreated={async (tokenId, last4, brand, details) => {
                    const tokenDetails = details || null;
                    setIsProcessing(true);
                    try {
                      // Process the payment with PayArc token
                      const isDonation = invoiceId.startsWith('donation-');
                      const endpoint = isDonation ? '/api/donations/process' : '/api/payments/charge';
                      const payload = isDonation ? {
                        companyId: invoice.companyId,
                        amount: totalWithFee,
                        currency: 'usd',
                        tokenId,
                        payarcTokenDetails: tokenDetails,
                        donationData: {
                          companyId: invoice.companyId,
                          amount: invoiceAmount,
                          firstName: invoice.customerName?.split(' ')[0] || '',
                          lastName: invoice.customerName?.split(' ')[1] || '',
                          email: invoice.customerEmail || '',
                          address: invoice.street || '',
                          city: invoice.city || '',
                          state: invoice.state || '',
                          zip: invoice.zip || '',
                          country: invoice.country || '',
                          platformFeeEnabled: enablePlatformFee,
                          platformFeeAmount: platformFeeAmount,
                          platformFeePercentage: platformFee,
                          paymentMethod: 'credit_card',
                          dynamicFields: invoice.dynamicFields || {},
                          slug: (invoice as any).slug || (invoice.dynamicFields?.slug) || '',
                          description: donationDescription,
                          metadata: {
                            source: 'payment_modal',
                            ...(hasInvoiceId ? { invoiceId } : {}),
                          }
                        }
                      } : {
                        companyId: invoice.companyId,
                        amount: totalWithFee,
                        currency: 'usd',
                        source: 'new',
                        tokenId,
                        description: isVirtualTerminalInvoice
                          ? virtualTerminalDescription
                          : 'Payment for Invoice #' + (invoice.invoiceNumber || ''),
                        orderId: `INV-${invoice.invoiceNumber}-${Date.now()}`,
                        ...(hasInvoiceId ? { invoiceId } : {}),
                        customerId: invoice.customerId,
                        customerName: invoice.customerName,
                        customerEmail: invoice.customerEmail,
                        savePaymentMethod: savePaymentMethod,
                        payarcTokenDetails: tokenDetails,
                        platformFeeEnabled: enablePlatformFee,
                        platformFeePercentage: platformFee,
                        platformFeeAmount: platformFeeAmount,
                        salesTax: taxAmount,
                        kind: 'card',
                        provider: 'payarc'
                      };

                      const apiResponse = await fetch(endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                      });

                      const responseData = await apiResponse.json();

                      if (responseData.success) {
                        // Show success notification
                        showTransactionSuccess({
                          transactionid: responseData.transactionId || responseData.transaction_id || '',
                          amount: totalWithFee,
                          cardLast4: last4,
                          additionalMessage: '',
                          onClose: () => {
                            // Reset processing state immediately
                            setIsProcessing(false);
                            onOpenChange(false);
                            if (onPaymentSuccess) {
                              onPaymentSuccess(responseData.transactionId || responseData.transaction_id || '', last4, 'credit_card');
                            }
                            if (invoiceId.startsWith('virtual-terminal-')) {
                              // For virtual terminal, just close modal (parent component will handle data refresh)
                              // No reload needed for SPA behavior
                            } else if (hasInvoiceId && !invoiceId.startsWith('donation-')) {
                              window.location.href = `/pay/${invoiceId}`;
                            }
                          },
                        });

                        // Save payment method if requested
                        if (savePaymentMethod && invoice.customerId) {
                          const fallbackDetails = tokenDetails || null;
                          await savePaymentMethodToFirebase(invoice.customerId, {
                            ...responseData,
                            last4,
                            cardType: brand,
                            provider: 'payarc',
                            payarcPaymentToken: responseData.payarcPaymentToken || fallbackDetails?.payarcTokenId,
                            payarcCustomerRef: responseData.payarcCustomerRef || fallbackDetails?.payarcCustomerId,
                            payarcChargeId: responseData.payarcChargeId || fallbackDetails?.payarcChargeId,
                            expMonth: responseData.expMonth || fallbackDetails?.expMonth,
                            expYear: responseData.expYear || fallbackDetails?.expYear,
                          });
                        }
                      } else {
                        setErrorCode(responseData.code || 'PAYMENT_ERROR');
                        setIsErrorDialogOpen(true);
                      }
                    } catch (error) {
                      console.error('PayArc payment error:', error);
                      setErrorCode('PROCESSING_ERROR');
                      setIsErrorDialogOpen(true);
                    } finally {
                      setIsProcessing(false);
                    }
                  }}
                  onError={(error) => {
                    showNotification({
                      title: 'Payment Error',
                      message: error,
                      type: 'error'
                    });
                    setErrorCode('TOKENIZATION_ERROR');
                    setIsErrorDialogOpen(true);
                  }}
                />
              </div>
            ) : (
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <Label htmlFor="card-number" className="block text-base font-semibold text-gray-700">
                  Card Information
                </Label>
                <div className="flex items-center text-sm text-gray-500">
                  <Lock size={14} className="mr-1" />
                  <span>Secure Payment</span>
                </div>
              </div>

              {/* Show loading indicator while CollectJS is initializing */}
              {!isCollectJSInitialized && defaultCardProvider === 'nmi' && (
                <div className="mb-5 h-48 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex flex-col items-center space-y-3">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-sm text-gray-600 font-medium">Initializing secure payment form...</p>
                    <p className="text-xs text-gray-500">Please wait a moment</p>
                  </div>
                </div>
              )}

              <div className={`mb-5 ${!isCollectJSInitialized && defaultCardProvider === 'nmi' ? 'hidden' : ''}`}>
                <div id="ccnumber" className="h-16 px-5 py-4 bg-white border border-gray-300 rounded-lg w-full"></div>
                <div id="ccnumber-error" className="mt-1 text-xs text-red-500"></div>
              </div>

              <div className={`grid grid-cols-2 gap-6 mb-5 ${!isCollectJSInitialized && defaultCardProvider === 'nmi' ? 'hidden' : ''}`}>
                <div>
                  <div id="ccexp" className="h-16 px-5 py-4 bg-white border border-gray-300 rounded-lg w-full"></div>
                  <div id="ccexp-error" className="mt-1 text-xs text-red-500"></div>
                </div>
                <div>
                  <div id="cvv" className="h-16 px-5 py-4 bg-white border border-gray-300 rounded-lg w-full"></div>
                  <div id="cvv-error" className="mt-1 text-xs text-red-500"></div>
                </div>
              </div>
              
              {/* Add checkbox for saving payment method for recurring/installment invoices */}
              {/* Only show checkbox when CollectJS is ready */}
              {(isCollectJSInitialized || defaultCardProvider !== 'nmi') && (
                <>
                  {isRecurringOrInstallment ? (
                    <div className="space-y-2 mt-4 p-4 border border-blue-100 bg-blue-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="save-payment-method" 
                          checked={savePaymentMethod}
                          onCheckedChange={(checked) => setSavePaymentMethod(checked === true)}
                          className="text-blue-600 border-blue-600"
                        />
                        <Label htmlFor="save-payment-method" className="text-sm font-medium text-gray-800">
                          Save payment method for future {invoice.invoiceType} payments
                        </Label>
                      </div>
                      <p className="text-xs text-blue-700 pl-6">
                        By checking this box, you authorize us to store your payment information securely and to automatically 
                        charge this payment method for future {invoice.invoiceType === 'recurring' ? 'recurring' : 'installment'} payments 
                        according to the schedule outlined in your invoice.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 mt-4">
                      <Checkbox 
                        id="save-payment-method" 
                        checked={savePaymentMethod}
                        onCheckedChange={(checked) => setSavePaymentMethod(checked === true)}
                        className="text-blue-600 border-blue-500"
                      />
                      <Label htmlFor="save-payment-method" className="text-sm text-gray-600">
                        Save payment method for future payments
                      </Label>
                    </div>
                  )}
                </>
              )}
            </div>
            )}
            
            {defaultCardProvider === 'nmi' && (
              <Button
                onClick={handleCheckout}
                disabled={isProcessing || !isCollectJSInitialized}
                className="w-full h-12 text-base font-medium rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Pay {formatCurrency(totalWithFee)}
                  </>
                )}
              </Button>
            )}
            
            <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-500">
              <Shield size={14} />
              <span>Payments are securely processed and encrypted</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <PaymentErrorDialog
        open={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
        errorCode={errorCode}
      />
    </>
  );
} 
